/**
 * Product copy for a failed sign-in.
 *
 * An identity provider can answer a denied sign-in with its own sentence
 * naming the account and organization ids, and Studio used to render it
 * verbatim. Studio never shows a person an account id or an organization id,
 * and that sentence does not tell them what to do about it either, so the
 * screen shows copy written for the failure instead.
 *
 * Errors nobody here recognizes fall back to the generic line rather than the
 * provider's text: that text is written for an operator reading a log, and the
 * next unrecognized variant is as likely to carry an id as this one was.
 * Anything that does still render a provider string elsewhere in the app goes
 * through `lib/sanitizeUserFacingError`, which drops ids as a backstop.
 *
 * Keep in sync with admin/src/auth/authErrorMessage.ts.
 */

export type AuthErrorKind =
  'organization-access-denied' | 'access-denied' | 'session-expired' | 'unknown';

export interface AuthErrorNotice {
  kind: AuthErrorKind;
  title: string;
  body: string;
  /**
   * The account itself is what was refused, so retrying with it cannot
   * succeed — the screen offers a way out to a different one.
   */
  canSwitchAccount: boolean;
}

const COPY_KEYS: Record<AuthErrorKind, { title: string; body: string }> = {
  'organization-access-denied': {
    title: 'auth.orgAccessDeniedTitle',
    body: 'auth.orgAccessDeniedBody',
  },
  'access-denied': { title: 'auth.accessDeniedTitle', body: 'auth.accessDeniedBody' },
  'session-expired': { title: 'auth.sessionExpiredTitle', body: 'auth.sessionExpiredBody' },
  unknown: { title: 'auth.errorTitle', body: 'auth.errorBody' },
};

/** OAuth errors (oidc-client-ts and the like) carry these fields alongside `message`. */
interface ProviderError {
  error?: unknown;
  error_description?: unknown;
  message?: unknown;
}

function providerFields(error: unknown): { code: string; detail: string } {
  const fields = (error ?? {}) as ProviderError;
  const code = typeof fields.error === 'string' ? fields.error.trim().toLowerCase() : '';
  const detail = [fields.error_description, fields.message]
    .map((value) => (typeof value === 'string' ? value : ''))
    .join(' ')
    .toLowerCase();
  return { code, detail };
}

/**
 * Org membership refusals, matched on the description because the OAuth code
 * is a plain `access_denied` that a dozen other rules also return. An id-shaped
 * `org_…` in a denial is itself the signal: only the org gate names one.
 */
const ORG_DENIAL_PATTERNS = [
  /\bnot a member of (?:the )?organization/,
  /\borganization\b[^.]*\b(?:membership|not a member|is not part)\b/,
  /\borg_[a-z0-9]{10,}\b/,
];

/** Codes that mean the session is gone rather than the account being refused. */
const SESSION_EXPIRED_CODES = new Set([
  'login_required',
  'missing_refresh_token',
  'invalid_grant',
  'consent_required',
  'interaction_required',
]);

export function classifyAuthError(error: unknown): AuthErrorKind {
  const { code, detail } = providerFields(error);
  if (ORG_DENIAL_PATTERNS.some((pattern) => pattern.test(detail))) {
    return 'organization-access-denied';
  }
  if (code === 'access_denied' || code === 'unauthorized' || /\baccess[_ ]denied\b/.test(detail)) {
    return 'access-denied';
  }
  if (
    SESSION_EXPIRED_CODES.has(code) ||
    /\blogin[_ ]required\b/.test(detail) ||
    /\brefresh token\b/.test(detail)
  ) {
    return 'session-expired';
  }
  return 'unknown';
}

/**
 * Safe to log: an OAuth error code names the decision the IdP made, never who
 * it was made about. The description that does name someone stays in the
 * identity provider's log, where support can read it.
 */
export function authErrorCode(error: unknown): string {
  return providerFields(error).code || 'unknown';
}

export function describeAuthError(
  error: unknown,
  translate: (key: string) => string,
): AuthErrorNotice {
  const kind = classifyAuthError(error);
  const keys = COPY_KEYS[kind];
  return {
    kind,
    title: translate(keys.title),
    body: translate(keys.body),
    canSwitchAccount: kind === 'organization-access-denied' || kind === 'access-denied',
  };
}
